export interface SearchResultsGraphQL<T> {
  pageInfo: {
    startCursor: string;
    endCursor: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  totalCount: number;
  edges: {
    cursor: string;
    node: T;
  }[];
}

export interface TypeDictionary {
  [index: string]: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const generateOrder = (request: any, hasDefaultOrder: boolean): string | null => {
  const defaultOrder = "{id: ASC}";
  if (request.sort?.length && request.sort[0].key && request.sort[0].direction) {
    let orderBlock = "order: [ ";
    for (let i = 0; i < request.sort?.length; i++) {
      if (request.sort[i].key && request.sort[i].direction) {
        if (i > 0) {
          orderBlock += ", ";
        }
        orderBlock += "{";
        const key = request.sort[i].key.replace("results.", "");
        const keys = key.split(".");
        for (let j = 0; j < keys.length; j++) {
          orderBlock += `${keys[j]}: `;
          if (j < keys.length - 1) orderBlock += "{ ";
        }
        orderBlock += request.sort[i].direction.toUpperCase();
        for (let j = 0; j < keys.length; j++) {
          orderBlock += " }";
        }
      }
    }
    if (hasDefaultOrder && !orderBlock.includes("{id:")) {
      orderBlock += `, ${defaultOrder}`;
    }
    orderBlock += " ]";
    return orderBlock;
  }
  return hasDefaultOrder ? `order: [${defaultOrder}]` : null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const generatePaging = (request: any): [string, string, any] | null => {
  if (request.paging) {
    let pagingBlock = `${request.paging.beforeCursor ? "last" : "first"}: $limit`;
    let pagingVarsBlock = "$limit: Int";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pagingVars: any = { limit: request.paging.limit };
    if (request.paging.beforeCursor) {
      pagingBlock += `
        before: $beforeCursor`;
      pagingVarsBlock += ", $beforeCursor: String";
      pagingVars.beforeCursor = request.paging.beforeCursor;
    }
    if (request.paging.afterCursor) {
      pagingBlock += `
        after: $afterCursor`;
      pagingVarsBlock += ", $afterCursor: String";
      pagingVars.afterCursor = request.paging.afterCursor;
    }
    return [pagingBlock, pagingVarsBlock, pagingVars];
  }
  return null;
};

const pathToCamelCase = (path: string[]): string => {
  let res = path[0];
  for (let i = 1; i < path.length; i++) res += path[i][0].toUpperCase() + path[i].substring(1);
  return res;
};

const FILTER_WRAPPER_KEYS = new Set(["some", "every", "none"]);

const resolveVariableType = (path: string[], key: string, typeDict: TypeDictionary): string | undefined => {
  const fullPath = path.concat(key);
  const fullPathKey = fullPath.join(".");

  if (typeDict[fullPathKey]) return typeDict[fullPathKey];

  const normalizedPathKey = fullPath.filter((pathPart) => !FILTER_WRAPPER_KEYS.has(pathPart)).join(".");
  return typeDict[normalizedPathKey];
};

const hasOwnProperty = (obj: Record<string, unknown>, key: string): boolean => {
  return Object.prototype.hasOwnProperty.call(obj, key);
};

const replaceFirstVariableReference = (block: string, currentName: string, nextName: string): string => {
  return block.replace(new RegExp(`\\$${currentName}(?![A-Za-z0-9_])`), `$${nextName}`);
};

const deduplicateVariableNames = (
  filterBlock: string,
  filterVarsBlock: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterVars: any,
  existingVars: Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): [string, string, any] => {
  if (!filterVarsBlock || filterVarsBlock.trim() === "") {
    return [filterBlock, filterVarsBlock, filterVars];
  }

  const definitions = filterVarsBlock
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);

  const dedupedDefinitions: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dedupedVars: any = {};
  let dedupedFilterBlock = filterBlock;

  definitions.forEach((definition) => {
    const match = definition.match(/^\$(\w+)\s*:\s*(.+)$/);
    if (!match) {
      dedupedDefinitions.push(definition);
      return;
    }

    const [, originalName, variableType] = match;
    let uniqueName = originalName;
    let duplicateIndex = 1;

    while (hasOwnProperty(existingVars, uniqueName) || hasOwnProperty(dedupedVars, uniqueName)) {
      uniqueName = `${originalName}${duplicateIndex}`;
      duplicateIndex += 1;
    }

    if (uniqueName !== originalName) {
      dedupedFilterBlock = replaceFirstVariableReference(dedupedFilterBlock, originalName, uniqueName);
    }

    dedupedDefinitions.push(`$${uniqueName}: ${variableType}`);

    if (hasOwnProperty(filterVars, originalName)) {
      dedupedVars[uniqueName] = filterVars[originalName];
    }
  });

  return [dedupedFilterBlock, dedupedDefinitions.join(", "), dedupedVars];
};

const recurseFilterKeys = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: any,
  path: string[],
  filterBlock: string,
  filterVarsBlock: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterVars: any,
  typeDict: TypeDictionary,
  isWrapped?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): [string, string, any] => {
  if (obj === null || obj === undefined) return [filterBlock, filterVarsBlock, filterVars];
  Object.keys(obj).forEach((key, idx) => {
    if (obj[key] === null || obj[key] === undefined) return;
    // Guard against non-terminating recursion: the `else` branch below recurses into any
    // nested object, so a primitive here (e.g. an operator string like "eq") would recurse
    // over its own characters forever. A criterion with an operator but no value also can't
    // be filtered on, so skip it rather than descending into its operator string. Legacy
    // `_group...` filters carry an operator + `_group_filter` (no `value`) and are handled
    // by their own branch below, so they must not be caught by the valueless-skip.
    if (typeof obj[key] !== "object") return;
    if (!key.startsWith("_group") && obj[key].operator !== undefined && obj[key].value === undefined) return;
    if (obj[key].operator !== undefined && obj[key].value !== undefined) {
      // this is an instance of FilterCriteria<T>
      const varName = pathToCamelCase(path.concat(key));

      if (isWrapped) {
        // eslint-disable-next-line no-param-reassign
        filterBlock += `{ ${key}: { ${obj[key].operator}: $${varName} } }`;
      } else {
        // eslint-disable-next-line no-param-reassign
        filterBlock += `${key}: { ${obj[key].operator}: $${varName} }`;
      }

      if (idx < Object.keys(obj).length - 1) {
        // eslint-disable-next-line no-param-reassign
        filterBlock += ", ";
      }

      // eslint-disable-next-line no-param-reassign
      filterVarsBlock += `$${varName}: ${resolveVariableType(path, key, typeDict)}, `;
      // eslint-disable-next-line no-param-reassign
      filterVars[varName] = obj[key].value;
      // eslint-disable-next-line no-underscore-dangle
    } else if (key.startsWith("_group") && obj[key].operator !== undefined && obj[key]._group_filter !== undefined) {
      /*
     Example: how to use 'or' operator inside an object
      filterResults = {
        ...filterResults,
        _group_objectNameAAA: {
          operator: "or",
          _group_filter: [
            {
              objectAAAFieldName1: {
                  operator: "eq",
                value: projectDetails?.group?.groupUuid || "",
              },
            },
            {
              objectAAAFieldName2: {
                operator: "eq",
                value: projectUuid,
              },
            },
          ],
        },
      };
    }
      or
    // eslint-disable-next-line no-underscore-dangle
    // filterCriteria._group_ = {
    //   operator: "or",
    //   _group_filter: activityStatuses.map((el) => {
    //     return {
    //       status: {
    //         operator: "eq",
    //         value: el,
    //       },
    //     };
    //   }),
    // };
      */
      const groupKey = key.substring(7);
      let endFilterBlock = ``;
      if (groupKey) {
        // eslint-disable-next-line no-param-reassign
        filterBlock += `{${groupKey}: {${obj[key].operator}: [`;
        endFilterBlock = `]}},`;
      } else {
        // eslint-disable-next-line no-param-reassign
        filterBlock += `${obj[key].operator}: [`;
        endFilterBlock = `]`;
      }

      // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
      let filterIndex = 0;
      // eslint-disable-next-line no-underscore-dangle
      obj[key]._group_filter.forEach((objInGrp: unknown) => {
        // eslint-disable-next-line no-param-reassign
        filterBlock += `{`;
        // eslint-disable-next-line no-param-reassign
        [filterBlock, filterVarsBlock, filterVars] = recurseFilterKeys(
          objInGrp,
          path.concat(groupKey || `_${filterIndex.toString()}_`), //
          filterBlock,
          filterVarsBlock,
          filterVars,
          typeDict
        );
        filterIndex += 1;
        // eslint-disable-next-line no-param-reassign
        filterBlock += `} ,`;
      });
      // remove last ","
      // eslint-disable-next-line no-param-reassign
      filterBlock = filterBlock.slice(0, -1);
      // eslint-disable-next-line no-param-reassign
      filterBlock += endFilterBlock;
    } else {
      if (isWrapped) {
        // eslint-disable-next-line no-param-reassign
        filterBlock += `{ ${key}: { `;
      } else {
        // eslint-disable-next-line no-param-reassign
        filterBlock += `${key}: { `;
      }
      // eslint-disable-next-line no-param-reassign
      [filterBlock, filterVarsBlock, filterVars] = recurseFilterKeys(
        obj[key],
        path.concat(key),
        filterBlock,
        filterVarsBlock,
        filterVars,
        typeDict
      );

      if (isWrapped) {
        // eslint-disable-next-line no-param-reassign
        filterBlock += " } }";
      } else {
        // eslint-disable-next-line no-param-reassign
        filterBlock += " }";
      }

      if (idx < Object.keys(obj).length - 1) {
        // eslint-disable-next-line no-param-reassign
        filterBlock += ", ";
      }
    }
  });
  return [filterBlock, filterVarsBlock, filterVars];
};

const generateFilter = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: any,
  typeDict: TypeDictionary
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): [string, string, any] | null => {
  if (request.filter?.results && Object.keys(request.filter.results).length > 0) {
    // eslint-disable-next-line prefer-const
    let [filterBlock, filterVarsBlock, filterVars] = recurseFilterKeys(
      request.filter.results,
      [],
      "",
      "",
      {},
      typeDict
    );

    filterVarsBlock = filterVarsBlock.substring(0, filterVarsBlock.length - 2); // remove trailing comma and space ", "
    return [filterBlock, filterVarsBlock, filterVars];
  }
  return null;
};

// Builds GraphQL filter groups, supporting nested groups (e.g. AND with nested OR groups).
interface GroupFilterBuildResult {
  operator: string;
  block: string;
  items: string;
  varBlock: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  vars: any;
}

const extractGroupItems = (groupOperator: string, groupBlock: string): string => {
  const prefix = `${groupOperator}: [ `;
  const suffix = " ]";

  if (groupBlock.startsWith(prefix) && groupBlock.endsWith(suffix)) {
    return groupBlock.substring(prefix.length, groupBlock.length - suffix.length);
  }

  return groupBlock;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildGroupFilter = (group: any, typeDict: TypeDictionary): GroupFilterBuildResult => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const blockParts: string[] = [];
  const varBlockParts: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let vars: any = {};

  if (group.filter?.results) {
    // eslint-disable-next-line prefer-const
    let [filterBlock, filterVarsBlock, filterVars] = recurseFilterKeys(
      group.filter.results,
      [],
      "",
      "",
      {},
      typeDict,
      true
    );

    if (filterVarsBlock.endsWith(", ")) {
      filterVarsBlock = filterVarsBlock.substring(0, filterVarsBlock.length - 2); // remove trailing comma and space ", "
    }

    [filterBlock, filterVarsBlock, filterVars] = deduplicateVariableNames(
      filterBlock,
      filterVarsBlock,
      filterVars,
      vars
    );

    blockParts.push(filterBlock);
    if (filterVarsBlock) {
      varBlockParts.push(filterVarsBlock);
    }
    vars = { ...vars, ...filterVars };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nestedGroups: any[] = [];
  if (group.filterGroups?.length) nestedGroups.push(...group.filterGroups);
  // Support group.filter containing nested groups directly (e.g. { operator: "and", filter: { operator: "or", filter: {...} } })
  if (group.filter && group.filter.operator && group.filter.filter && !group.filter.results) {
    nestedGroups.push(group.filter);
  }

  nestedGroups.forEach((nestedGroup) => {
    let nestedGroupFilter = buildGroupFilter(nestedGroup, typeDict);
    let nestedBlock = nestedGroupFilter.block;
    let nestedVarsBlock = nestedGroupFilter.varBlock;
    let nestedVars = nestedGroupFilter.vars;

    [nestedBlock, nestedVarsBlock, nestedVars] = deduplicateVariableNames(
      nestedBlock,
      nestedVarsBlock,
      nestedVars || {},
      vars
    );

    nestedGroupFilter = {
      ...nestedGroupFilter,
      block: nestedBlock,
      items: extractGroupItems(nestedGroupFilter.operator, nestedBlock),
      varBlock: nestedVarsBlock,
      vars: nestedVars,
    };

    blockParts.push(nestedBlock);
    if (nestedVarsBlock) {
      varBlockParts.push(nestedVarsBlock);
    }
    vars = { ...vars, ...nestedVars };
  });

  const formattedBlocks = blockParts
    .map((bp) => {
      const trimmed = bp.trim();
      return trimmed.startsWith("{") ? trimmed : `{ ${trimmed} }`;
    })
    .join(", ");
  const operator = group.operator.toLowerCase();
  const filterBlockWithOperator = `${operator}: [ ${formattedBlocks} ]`;
  const varBlock = varBlockParts.join(", ");

  return {
    operator,
    block: filterBlockWithOperator,
    items: formattedBlocks,
    varBlock,
    vars,
  };
};

const generateFilterFromGroup = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: any,
  typeDict: TypeDictionary
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): GroupFilterBuildResult[] | null => {
  if (request.filterGroups && request.filterGroups.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return request.filterGroups.map((group: any) => buildGroupFilter(group, typeDict));
  }
  return null;
};

/**
 * Generates GraphQL query helper blocks (`where`, `paging`, `sort`) based on a request object.
 *
 * @param req - The request object containing filter, sort, and paging details.
 * @param typeDict - A dictionary mapping request keys to GraphQL types.
 * @param defaultOrder - Boolean indicating whether to add a default sort order by id.
 * @returns An array containing the formatted query block, variable definitions block, and variables object.
 */
export const generateGraphQLHelpers = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  req: any,
  typeDict: TypeDictionary,
  defaultOrder = true
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): [string, string, any] => {
  const orderBlock = generateOrder(req, defaultOrder);
  const [pagingBlock, pagingVarsBlock, pagingVars] = generatePaging(req) || [null, null, null];
  let [filterBlock, filterVarsBlock, filterVars] = generateFilter(req, typeDict) || [null, null, null];
  const filterGroups = generateFilterFromGroup(req, typeDict);

  let varBlock = "";
  let formattedFilterBlock = "";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let formattedFilterVars: any = {};

  if (filterBlock && filterBlock.length > 0) {
    [filterBlock, filterVarsBlock, filterVars] = deduplicateVariableNames(
      filterBlock,
      filterVarsBlock || "",
      filterVars || {},
      formattedFilterVars
    );

    formattedFilterBlock += filterBlock;
    if (filterVarsBlock) {
      varBlock += filterVarsBlock;
    }
    formattedFilterVars = { ...formattedFilterVars, ...filterVars };
  }

  if (filterGroups) {
    const groupFilterBlocks: Record<string, string[]> = {};

    filterGroups.forEach((el) => {
      let groupFilterBlock = el.block;
      let groupVarBlock = el.varBlock;
      let groupVars = el.vars;

      [groupFilterBlock, groupVarBlock, groupVars] = deduplicateVariableNames(
        groupFilterBlock,
        groupVarBlock,
        groupVars || {},
        formattedFilterVars
      );

      const groupItems = extractGroupItems(el.operator, groupFilterBlock);
      groupFilterBlocks[el.operator] = [...(groupFilterBlocks[el.operator] || []), groupItems];

      if (groupVarBlock) {
        if (varBlock.length > 0) {
          varBlock += ", ";
        }
        varBlock += groupVarBlock;
      }

      formattedFilterVars = { ...formattedFilterVars, ...groupVars };
    });

    const mergedGroupFilterBlocks = Object.entries(groupFilterBlocks).map(
      ([groupOperator, groupItems]) => `${groupOperator}: [ ${groupItems.join(", ")} ]`
    );

    if (mergedGroupFilterBlocks.length > 0) {
      if (formattedFilterBlock.length > 0) {
        formattedFilterBlock += ", ";
      }
      formattedFilterBlock += mergedGroupFilterBlocks.join(", ");
    }
  }

  if (formattedFilterBlock !== "") {
    formattedFilterBlock = `where: { ${formattedFilterBlock} }`;
  }

  const sortFilterPagingBlock = `${orderBlock || ""}
        ${formattedFilterBlock}
        ${pagingBlock || ""}
  `;

  if (varBlock.length > 0 && pagingVarsBlock) varBlock += ", ";
  if (pagingVarsBlock) varBlock += pagingVarsBlock;

  let variables = {};
  if (pagingVars) variables = { ...variables, ...pagingVars };
  if (formattedFilterVars) variables = { ...variables, ...formattedFilterVars };

  return [sortFilterPagingBlock, varBlock, variables];
};
